import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Download, Smartphone } from 'lucide-react';

export const PWAInstallPrompt = () => {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [showPrompt, setShowPrompt] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [isStandalone, setIsStandalone] = useState(false);

    useEffect(() => {
        // Check if already installed
        const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches 
            || window.navigator.standalone 
            || document.referrer.includes('android-app://');
        setIsStandalone(isInStandaloneMode);

        // Check if iOS
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
        setIsIOS(iOS);

        // Listen for beforeinstallprompt event (Chrome/Edge/Android)
        const handleBeforeInstallPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            
            // Check if user has dismissed before
            const dismissed = localStorage.getItem('pwa-prompt-dismissed');
            if (!dismissed) {
                setTimeout(() => setShowPrompt(true), 3000); // Show after 3 seconds
            }
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

        // For iOS, show prompt after delay if not dismissed
        if (iOS && !isInStandaloneMode) {
            const dismissed = localStorage.getItem('pwa-prompt-dismissed');
            if (!dismissed) {
                setTimeout(() => setShowPrompt(true), 5000);
            }
        }

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        };
    }, []);

    const handleInstall = async () => {
        if (deferredPrompt) {
            deferredPrompt.prompt();
            const { outcome } = await deferredPrompt.userChoice;
            console.log(`User ${outcome} the install prompt`);
            setDeferredPrompt(null);
            setShowPrompt(false);
        }
    };

    const handleDismiss = () => {
        setShowPrompt(false);
        localStorage.setItem('pwa-prompt-dismissed', 'true');
    };

    // Don't show if already installed
    if (isStandalone || !showPrompt) {
        return null;
    }

    return (
        <div className="fixed bottom-24 left-4 right-4 md:left-auto md:right-6 md:w-80 bg-card border border-border rounded-xl shadow-2xl p-4 z-50 animate-in slide-in-from-bottom-5">
            <button 
                onClick={handleDismiss}
                className="absolute top-2 right-2 p-1 hover:bg-muted rounded-full"
            >
                <X className="w-4 h-4 text-muted-foreground" />
            </button>
            
            <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm">Install Autoimmune Pathways</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        {isIOS 
                            ? "Tap the share button, then 'Add to Home Screen'" 
                            : "Get quick access and receive notifications even when the app is closed"}
                    </p>
                </div>
            </div>
            
            {!isIOS && deferredPrompt && (
                <Button 
                    onClick={handleInstall} 
                    className="w-full mt-3 gap-2"
                    size="sm"
                >
                    <Download className="w-4 h-4" />
                    Install App
                </Button>
            )}
            
            {isIOS && (
                <div className="mt-3 p-2 bg-muted rounded-lg">
                    <p className="text-xs text-center">
                        Tap <span className="inline-block mx-1">
                            <svg className="w-4 h-4 inline" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M10 2a1 1 0 011 1v6.586l2.293-2.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 9.586V3a1 1 0 011-1z" />
                                <path d="M3 15a2 2 0 012-2h10a2 2 0 012 2v2a2 2 0 01-2 2H5a2 2 0 01-2-2v-2z" />
                            </svg>
                        </span> then "Add to Home Screen"
                    </p>
                </div>
            )}
        </div>
    );
};

export default PWAInstallPrompt;
